import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import Logo from './Logo';

const SECTIONS = [
  { id: 'studio', label: 'Studio' },
  { id: 'services', label: 'Services' },
  { id: 'approach', label: 'Approach' },
  { id: 'contact', label: 'Contact' },
];

const Nav: React.FC = () => {
  const { pathname } = useLocation();
  const isHome = pathname === '/';
  const [scrolled, setScrolled] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  React.useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const href = (id: string) => (isHome ? `#${id}` : `/#${id}`);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'border-b border-white/10 bg-ink-950/80 backdrop-blur-xl'
          : 'border-b border-transparent'
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-edge items-center justify-between px-6 lg:px-10">
        <Link to="/" aria-label="Parnasa Apps — home" className="group">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-9 md:flex" aria-label="Main">
          {SECTIONS.map((section) => (
            <a
              key={section.id}
              href={href(section.id)}
              className="group relative font-mono text-[11px] uppercase tracking-label text-paper/55 transition-colors hover:text-paper"
            >
              {section.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-peri-400 transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`mailto:contact@parnasapps.com`}
            className="hidden items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-mono text-[11px] uppercase tracking-label text-paper transition-all duration-300 hover:border-peri-400/60 hover:bg-peri-400/10 sm:inline-flex"
          >
            Start a project
            <ArrowUpRight size={13} strokeWidth={1.6} />
          </a>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-paper transition-colors hover:border-peri-400/60 md:hidden"
          >
            {open ? <X size={17} strokeWidth={1.6} /> : <Menu size={17} strokeWidth={1.6} />}
          </button>
        </div>
      </div>

      {/* Mobile sheet */}
      <div
        className={`overflow-hidden border-t border-white/10 bg-ink-950/97 backdrop-blur-xl transition-[max-height,opacity] duration-500 md:hidden ${
          open ? 'max-h-[420px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <nav className="flex flex-col px-6 py-4" aria-label="Mobile">
          {SECTIONS.map((section, i) => (
            <a
              key={section.id}
              href={href(section.id)}
              onClick={() => setOpen(false)}
              className="flex items-baseline justify-between border-b border-white/[0.07] py-4 text-lg text-paper/85 transition-colors hover:text-paper"
            >
              {section.label}
              <span className="font-mono text-[10px] tracking-label text-paper/55">
                {String(i + 1).padStart(2, '0')}
              </span>
            </a>
          ))}
          <a
            href="mailto:contact@parnasapps.com"
            onClick={() => setOpen(false)}
            className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-brand-sweep px-6 py-3.5 font-mono text-[11px] uppercase tracking-label text-ink-950"
          >
            Start a project
            <ArrowUpRight size={14} strokeWidth={2} />
          </a>
        </nav>
      </div>
    </header>
  );
};

export default Nav;
